import { useState, useEffect, useRef, useReducer } from 'react';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useNavigate } from 'react-router-dom';
import { startOutboundCall } from '@/domain/callFlow';
import { recordContactAttempt } from '@/domain/sequenceFlow';
import { pushLeadToAgent, type PushLeadInput, type PushLeadResult } from '@/domain/pushToAgentFlow';
import { buildPushSummary } from '@/utils/pushSummary';
import { isPlaceholderName, isWithAgent } from '@atink/domain';
import {
  customerName,
  extractAuPhone,
  extractCallbackTime,
  simTimeShort,
} from '@/utils/format';
import type { CustomerAction } from '@/components/CustomerActionModals';
import DepositLinkModal from '@/components/DepositLinkModal';
import { useSessionStore } from '@/state/sessionStore';
import { canCancelBooking } from '@/domain/opsCancelFlow';
import type { ConversationEntry, CustomerRecord } from '@/domain/types';
import { dmSendMode } from '@/domain/dmSendMode';
import { dmHandle, instagramDmUrl } from '@/domain/instagram';
import { sendDmIntent, sendErrorCopy } from '@/api/intents';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { useComposerDraftStore } from '@/state/composerDraftStore';
import {
  Send,
  Phone,
  Wallet,
  Hash,
  MoreHorizontal,
  MessageSquare,
  AtSign,
  Mail,
  ChevronDown,
  ScrollText,
  Sparkles,
  Calendar,
  XCircle,
  ListPlus,
  Check,
  ArrowRight,
  Copy,
  UserRound,
  Lock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

type MessageChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

interface Props {
  customer: CustomerRecord;
  onOpenModal?: (action: CustomerAction) => void;
  initialChannel?: MessageChannel;
  /**
   * Prefills the composer body - used by the confirmations work modal to seed
   * a follow-up draft after a no-answer outcome. Re-applies whenever the value
   * changes (e.g. the operator picks "No answer" after the composer has
   * already mounted); does not clobber further edits once set, since the
   * dependency only changes when a fresh draft is supplied.
   */
  initialDraft?: string;
  /**
   * Where this composer is mounted. Drives which actions appear, via
   * ACTIONS_BY_HOST below - the composer is shared by four surfaces whose
   * relevant actions genuinely differ, and one named table beats threading a
   * boolean per button. Omitted means every action shows.
   */
  host?: ComposerHost;
}

/** The surfaces that mount this composer. */
export type ComposerHost = 'queue' | 'customer-record' | 'confirmations' | 'messenger';

interface ComposerActions {
  deposit: boolean;
  selfBook: boolean;
  pushToQueue: boolean;
  /** The outbound-call trigger. Off in the messenger (IG/FB DM threads only). */
  call: boolean;
}

const ALL_ACTIONS: ComposerActions = { deposit: true, selfBook: true, pushToQueue: true, call: true };

/**
 * Which actions each host shows. Nothing is deleted - a host that turns an action
 * off just doesn't render it, and flipping one back on is a single edit here.
 *
 * Self-book is off on every agent surface: the artist taking the piece generates
 * that link, not the front desk.
 */
const ACTIONS_BY_HOST: Record<ComposerHost, ComposerActions> = {
  // My Queue work modal. The client is already in the agent's queue, so pushing
  // them to it again is meaningless (and the modal header already shows their
  // sequence stage). Deposits come off the availability pane, grounded to a
  // pinned slot.
  queue: { deposit: false, selfBook: false, pushToQueue: false, call: true },
  // Front desk customer record. SlotDepositPane sits directly below this composer
  // on that screen and sends a deposit grounded to a picked slot, so the composer's
  // ungrounded duplicate goes the same way it did in the queue. Still the one
  // surface that hands a fresh lead to a booking agent, and calling is fine here
  // (the record may carry a phone).
  'customer-record': { deposit: false, selfBook: false, pushToQueue: true, call: true },
  // Conversations inbox (IG/FB DM threads only). Mirrors the customer record, but
  // calling is off: these threads are DM-only, so an outbound-call trigger doesn't
  // belong here (System Diagram 03 - phone intake lives on My Queue).
  messenger: { deposit: false, selfBook: false, pushToQueue: true, call: false },
  // Confirmations work modal: working an existing booking, but a client who wants
  // to rebook still needs handing over.
  confirmations: { deposit: true, selfBook: false, pushToQueue: true, call: true },
};

const CHANNEL_OPTIONS: Array<{
  value: MessageChannel;
  label: string;
  Icon: typeof MessageSquare;
}> = [
  { value: 'sms', label: 'SMS', Icon: MessageSquare },
  { value: 'meta-dm', label: 'Meta DM', Icon: MessageSquare },
  { value: 'ig-dm', label: 'Instagram DM', Icon: AtSign },
  { value: 'email', label: 'Email', Icon: Mail },
];

const TEMPLATES = [
  { id: 'deposit-nudge', label: 'Deposit nudge', body: 'Hey {first}, just following up on the deposit link. Want me to extend the expiry?' },
  { id: 'reschedule-offer', label: 'Reschedule offer', body: 'Hi {first}, I can move your session. What day works for you?' },
  { id: 'pre-session-prep', label: 'Pre-session prep', body: 'Hey {first}, quick reminder for your session: eat before, stay hydrated, and bring photo ID.' },
  { id: 'booking-confirmation', label: 'Booking confirmation', body: 'You\'re booked, {first}. We\'ll text you a reminder two days before.' },
  { id: 'reactivation-tap', label: 'Reactivation tap', body: 'Hey {first}, long time no see. Saw your old design intent on file, want me to lock something in?' },
];

export default function CustomerComposer({
  customer,
  onOpenModal,
  initialChannel,
  initialDraft,
  host,
}: Props) {
  const actions = host ? ACTIONS_BY_HOST[host] : ALL_ACTIONS;
  const navigate = useNavigate();
  const addConvo = useBrainStore((s) => s.addConversationEntry);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);
  // Only Ops cancels (W5b Q4); everyone else asks Ops from the same menu item.
  const canCancel = canCancelBooking(useSessionStore((s) => s.persona));
  const sequence = useBrainStore((s) => s.sequenceState[customer.id]);

  const inferredChannel: MessageChannel = (() => {
    const last = customer.conversation[customer.conversation.length - 1];
    if (
      last?.channel === 'meta-dm' ||
      last?.channel === 'ig-dm' ||
      last?.channel === 'sms' ||
      last?.channel === 'email'
    ) {
      return last.channel;
    }
    return 'sms';
  })();

  const [channel, setChannel] = useState<MessageChannel>(initialChannel ?? inferredChannel);
  const [body, setBody] = useState(initialDraft ?? '');
  const [channelOpen, setChannelOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pushOpen, setPushOpen] = useState(false);
  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [sending, setSending] = useState(false);
  // The last real-DM send that didn't land (network/5xx, or a handoff whose copy
  // failed). Pressing send again for the same customer/text/path/template is a
  // RETRY of that action, so it reuses its clientMessageId and the server dedupes
  // it (tv-isee). The key carries the customer: MessengerInbox reuses one
  // composer instance across threads.
  const pendingSend = useRef<{ clientMessageId: string; key: string } | null>(null);
  // Synchronous in-flight guard: a double-click lands before `sending` re-renders.
  const inFlight = useRef(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  // Forces a re-render when the click-time guard finds the window has moved.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const channelRef = useRef<HTMLDivElement>(null);
  const tmplRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const pushRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (channelRef.current && !channelRef.current.contains(e.target as Node)) setChannelOpen(false);
      if (tmplRef.current && !tmplRef.current.contains(e.target as Node)) setTemplatesOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
      if (pushRef.current && !pushRef.current.contains(e.target as Node)) setPushOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPushOpen(false);
    };
    window.addEventListener('mousedown', handler);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', handler);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // A fresh draft (e.g. the confirmations work modal revealing its no-answer
  // follow-up after the composer already mounted) re-seeds the body. Only
  // fires when the draft string itself changes, so it never clobbers an
  // operator's edits mid-typing.
  useEffect(() => {
    if (initialDraft) setBody(initialDraft);
  }, [initialDraft]);

  // Push-gating keys on a GENUINE push (the shared isWithAgent rule: a sequence
  // record with a sourceConversationId, or the durable pushed marker), not bare
  // record existence: a merely-contacted lead has a record but no source, and
  // must stay pushable. Anas 8 Oct: once pushed, the lead is the Agent's for
  // good, so a red or booked lead is never pushable again.
  const pushed = isWithAgent(customer, sequence);
  // The flipped button's label: "In queue · sequence N" (or "red") while the
  // Agent's sequence runs; "With the Agent" once a booking ended it.
  const inQueueLabel = !pushed
    ? null
    : sequence?.sourceConversationId
      ? `In queue · ${sequence.stage === 'red' ? 'red' : `sequence ${sequence.stage}`}`
      : 'With the Agent';

  const channelSpec = CHANNEL_OPTIONS.find((c) => c.value === channel) ?? CHANNEL_OPTIONS[0]!;

  // Wall time, not the sim clock: Meta's reply windows are real time (tv-isee).
  const dm = isStoreFallbackEnabled()
    ? { mode: 'simulated' as const, hoursLeft: 0 }
    : dmSendMode(customer, channel, new Date().toISOString());
  const templateLabel = TEMPLATES.find((t) => t.id === templateId)?.label;
  const agentDaysLeft = Math.max(1, Math.floor(dm.hoursLeft / 24));

  // A draft handed over by a failed-message chip or a suggestion ("Use reply").
  const draft = useComposerDraftStore((s) => s.draft);
  const clearDraft = useComposerDraftStore((s) => s.clear);
  // The deposit a loaded draft belongs to (tv-isee), kept only as long as that
  // draft's text is still in the box: cleared on a successful send or when the
  // body is emptied, so a later unrelated message never carries a stale id.
  const [draftDepositId, setDraftDepositId] = useState<string | undefined>(undefined);
  // MessengerInbox reuses one composer across threads. A deposit draft belongs
  // to its customer, so switching customers drops it (id, text and template).
  // Declared before the draft loader so a draft for the new customer still lands.
  const draftCustomerRef = useRef(customer.id);
  useEffect(() => {
    if (draftCustomerRef.current === customer.id) return;
    draftCustomerRef.current = customer.id;
    if (draftDepositId) {
      setBody('');
      setTemplateId(undefined);
      setDraftDepositId(undefined);
    }
  }, [customer.id, draftDepositId]);
  useEffect(() => {
    if (draft && draft.customerId === customer.id) {
      setBody(draft.body);
      setTemplateId(draft.templateId);
      setDraftDepositId(draft.depositId);
      clearDraft();
    }
  }, [draft, customer.id, clearDraft]);

  const onSend = async () => {
    const text = body.trim();
    if (!text || sending || inFlight.current) return;
    // Click-time guard: the window may have moved since this render (e.g. the 24h
    // mark passed while typing). Never switch the path silently; show the new
    // state and let the person send again.
    const nowMode = isStoreFallbackEnabled()
      ? 'simulated'
      : dmSendMode(customer, channel, new Date().toISOString()).mode;
    if (nowMode !== dm.mode) {
      pushNotification({ intensity: 'high', title: 'The reply window just changed — check the message and send again', body: customerName(customer), ttlMs: 5000 });
      rerender();
      return;
    }
    if (dm.mode === 'simulated') return onSendSimulated();
    const path = dm.mode;

    // One id per send action; a retry of the same action reuses it. The deposit
    // id is part of the key so a different deposit never replays another one's
    // clientMessageId.
    const key = JSON.stringify([customer.id, text, path, templateId ?? null, draftDepositId ?? null]);
    const clientMessageId =
      pendingSend.current?.key === key ? pendingSend.current.clientMessageId : crypto.randomUUID();
    pendingSend.current = { clientMessageId, key };

    inFlight.current = true;
    setSending(true);
    try {
      let copy: Promise<boolean> = Promise.resolve(true);
      if (path === 'handoff') {
        // Start the copy while this tab has focus, then open Instagram synchronously
        // inside the click gesture (popup blockers). Await the copy afterwards.
        copy = (navigator.clipboard?.writeText(text).then(() => true) ?? Promise.resolve(false)).catch(() => false);
        window.open(instagramDmUrl(customer.instagramHandle), '_blank', 'noopener');
      }
      const res = await sendDmIntent({
        customerId: customer.id,
        text,
        path,
        clientMessageId,
        ...(templateId ? { templateId } : {}),
        ...(draftDepositId ? { depositId: draftDepositId } : {}),
      });
      const copied = await copy;
      if (!res.ok) {
        // Only a network error or a 5xx is worth retrying under the same id.
        if (!(res.status === 0 || res.status >= 500)) pendingSend.current = null;
        pushNotification({ intensity: 'high', title: 'Message not saved', body: sendErrorCopy(res), ttlMs: 5000 });
        return;
      }
      // A handoff whose copy failed keeps its id: pressing again replays the same
      // row instead of recording a second unconfirmed one.
      pendingSend.current = copied ? null : pendingSend.current;
      // Server-authored: add to the store WITHOUT the bridge re-PUTting the customer.
      // A replay returns the same row id, so never append it twice.
      const already = useBrainStore.getState().customers[customer.id]?.conversation.some((e) => e.id === res.entry.id);
      if (!already) withServerAuthoredWrites([['customers', customer.id]], () => addConvo(customer.id, res.entry));
      const handle = dmHandle(customer.instagramHandle);
      const who = handle ? '@' + handle : customer.firstName;
      const titles: Record<string, string> = {
        sent: 'Sent to Instagram',
        sending: 'Sending…',
        failed: 'Not sent',
        unknown: 'Check Instagram before resending',
        handoff_unconfirmed: copied
          ? `Copied. Paste it into your Instagram chat with ${who}.`
          : `Couldn't copy. Copy your message and paste it into your Instagram chat with ${who}.`,
      };
      const calm = res.deliveryStatus === 'sent' || res.deliveryStatus === 'sending' || (res.deliveryStatus === 'handoff_unconfirmed' && copied);
      pushNotification({ intensity: calm ? 'info' : 'high', title: titles[res.deliveryStatus] ?? 'Sent', body: customerName(customer), ttlMs: 4000 });
      // Keep the text if the clipboard failed, so it can still be copied by hand.
      if (copied) {
        setBody('');
        setTemplateId(undefined);
        setDraftDepositId(undefined);
      }
      if (sequence || host === 'queue') recordContactAttempt(customer.id);
    } finally {
      inFlight.current = false;
      setSending(false);
      bodyRef.current?.focus();
    }
  };

  const onSendSimulated = () => {
    if (!body.trim()) return;
    const entry: ConversationEntry = {
      id: `msg-${Date.now()}`,
      customerId: customer.id,
      channel,
      direction: 'out',
      body,
      timestamp: clock,
      authorRole: 'agent',
      authorName: 'You',
    };
    addConvo(customer.id, entry);
    appendActivity({
      id: `act-msg-${entry.id}`,
      type: channel === 'sms' ? 'sms-sent' : 'meta-dm',
      customerId: customer.id,
      body: `${channelSpec.label} sent to ${customerName(customer)}.`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'info',
      title: `${channelSpec.label} sent`,
      body: `Delivered to ${customerName(customer)}.`,
      ttlMs: 3500,
    });
    setBody('');
    // A sent message counts as a contact attempt: it advances an existing
    // sequence, and from the queue it also STARTS one for a raw lead (a missed
    // call / fresh enquiry has no sequence yet), so contacting it makes it go
    // dormant and move to "Completed this sequence" (bd tv-jpx). Outside the
    // queue we keep the guard so casual record-page messages never mint a
    // sequence.
    if (sequence || host === 'queue') recordContactAttempt(customer.id);
  };

  const onCall = () => {
    startOutboundCall({ customerId: customer.id, studioId: customer.primaryStudioId });
  };

  const [depositModalOpen, setDepositModalOpen] = useState(false);

  return (
    <>
    <footer
      className="border-t border-gold-dim/20 bg-ink-deep sticky bottom-0"
      data-testid="customer-composer"
    >
        {dm.mode === 'human_agent' || dm.mode === 'handoff' ? (
          <div
            className={clsx(
              'mx-6 mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px] leading-snug text-cream',
              dm.mode === 'human_agent' ? 'border-orange/40 bg-orange/10' : 'border-gold-dim/40 bg-ink-soft/40',
            )}
            data-testid="composer-dm-notice"
            role="status"
          >
            {dm.mode === 'human_agent' ? (
              <UserRound size={14} className="mt-0.5 shrink-0 text-orange" />
            ) : (
              <Lock size={14} className="mt-0.5 shrink-0 text-cream-soft" />
            )}
            {dm.mode === 'human_agent' ? (
              <span>
                {customer.firstName} last messaged more than 24 hours ago. For {agentDaysLeft} more
                {agentDaysLeft === 1 ? ' day' : ' days'} you can still reply as a <b>human agent</b>. It sends under your name, so you need to write or check each message yourself.
              </span>
            ) : (
              <span>
                Instagram doesn't let apps message someone more than 7 days after their last message. Type your message here and we'll copy it and open Instagram so you can paste and send it. It's saved here as <b>not confirmed</b>. Once {customer.firstName} replies, you can send from here again.
              </span>
            )}
          </div>
        ) : null}
        <div className="px-6 pt-3 pb-1 flex items-center gap-2 flex-wrap">
          {/* Channel selector */}
          <div ref={channelRef} className="relative">
            <button
              type="button"
              onClick={() => setChannelOpen((v) => !v)}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-bright/50 text-gold-bright hover:bg-gold-bright/10 text-xs"
              data-testid="composer-channel"
            >
              <channelSpec.Icon size={12} /> {channelSpec.label}
              <ChevronDown size={10} />
            </button>
            {channelOpen ? (
              <div className="absolute bottom-full mb-2 left-0 z-30 panel py-1 min-w-[160px]">
                {CHANNEL_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setChannel(opt.value);
                      setChannelOpen(false);
                    }}
                    className={clsx(
                      'flex w-full items-center gap-2 px-3 py-1.5 text-xs',
                      channel === opt.value ? 'text-gold-bright' : 'text-cream-soft hover:text-cream',
                    )}
                    data-testid={`composer-channel-${opt.value}`}
                  >
                    <opt.Icon size={12} /> {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Templates */}
          <div ref={tmplRef} className="relative">
            <button
              type="button"
              onClick={() => setTemplatesOpen((v) => !v)}
              className="btn-quiet text-[11px]"
              data-testid="composer-templates"
            >
              Templates <ChevronDown size={10} />
            </button>
            {templatesOpen ? (
              <div className="absolute bottom-full mb-2 left-0 z-30 panel py-1 min-w-[240px]">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setBody(t.body.replace('{first}', customer.firstName));
                      setTemplateId(t.id);
                      setTemplatesOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 text-xs text-cream-soft hover:text-cream hover:bg-ink-soft/40"
                  >
                    <span className="block font-inter text-cream">{t.label}</span>
                    <span className="block font-serif text-[10px] text-cream-soft/70 mt-0.5 truncate">
                      {t.body.replace('{first}', customer.firstName).slice(0, 60)}…
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {dm.mode === 'human_agent' && templateLabel ? (
            <span className="rounded-md bg-gold/15 px-2 py-0.5 text-[11px] text-gold" data-testid="composer-template-chip">
              Template: {templateLabel} · check before sending
            </span>
          ) : null}

          <span className="h-4 w-px bg-gold-dim/40 mx-1" />

          {/* Inline action icons */}
          {actions.call ? (
            <button
              type="button"
              onClick={onCall}
              className="btn-ghost text-[11px]"
              data-testid="composer-call"
              title="Start outbound call"
            >
              <Phone size={12} /> Call
            </button>
          ) : null}
          {actions.deposit ? (
            <button
              type="button"
              onClick={() => setDepositModalOpen(true)}
              className="btn-ghost text-[11px]"
              data-testid="composer-deposit"
              title="Send Stripe deposit link"
            >
              <Wallet size={12} /> Deposit link
            </button>
          ) : null}
          {actions.selfBook ? (
            <button
              type="button"
              onClick={() =>
                navigate('/self-book-generator', { state: { customerId: customer.id } })
              }
              className="btn-ghost text-[11px]"
              data-testid="composer-self-book"
              title="Generate a self-book link"
            >
              <Hash size={12} /> Self-book
            </button>
          ) : null}

          {/* Push to agent queue — hands the conversation to a booking agent, so it is
              separated from the message-send actions and pushed to the right.
              Hidden in the queue modal: the agent working it IS the booking agent
              it would hand to. */}
          {actions.pushToQueue ? (
          <div ref={pushRef} className="relative ml-auto flex items-center gap-2">
            <span className="h-4 w-px bg-gold-dim/40" />
            {inQueueLabel ? (
              <button
                type="button"
                onClick={() => setPushOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-mint/50 text-mint text-[11px]"
                data-testid="composer-in-queue"
                title="Already handed to a booking agent"
              >
                <Check size={12} /> {inQueueLabel}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setPushOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-bright/55 bg-gold/10 text-gold-bright text-[11px] uppercase tracking-wider"
                data-testid="composer-push"
                title="Hand this lead to a booking agent"
              >
                <ListPlus size={12} /> Push to agent queue
              </button>
            )}
            {pushOpen ? (
              pushed ? (
                <PushDuplicate
                  label={inQueueLabel ?? 'In queue'}
                  onJump={() => {
                    setPushOpen(false);
                    navigate('/queue');
                  }}
                />
              ) : (
                <PushForm
                  customer={customer}
                  clock={clock}
                  onCancel={() => setPushOpen(false)}
                  onConfirm={(payload) => {
                    const res = pushLeadToAgent(payload);
                    if (!res.ok) return res.reason;
                    setPushOpen(false);
                    pushNotification({
                      intensity: 'info',
                      title: 'Pushed to queue',
                      body: `${payload.firstName ? `${payload.firstName} ${payload.lastName ?? ''}`.trim() : customerName(customer)} handed to a booking agent.`,
                      ttlMs: 3500,
                    });
                    return null;
                  }}
                />
              )
            ) : null}
          </div>
          ) : null}

          {/* More menu. Pushed right on its own when the queue cluster is hidden,
              since that cluster was carrying the ml-auto. */}
          <div ref={moreRef} className={clsx('relative', !actions.pushToQueue && 'ml-auto')}>
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              className="btn-quiet text-[11px]"
              data-testid="composer-more"
              title="More actions"
            >
              <MoreHorizontal size={14} />
            </button>
            <AnimatePresence>
              {moreOpen ? (
                <motion.div
                  initial={{ y: 4, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 4, opacity: 0 }}
                  className="absolute bottom-full mb-2 right-0 z-30 panel py-1 min-w-[200px]"
                >
                  <MoreItem
                    Icon={ScrollText}
                    label="Rapport note"
                    testid="composer-more-rapport"
                    onClick={() => {
                      onOpenModal?.('rapport');
                      setMoreOpen(false);
                    }}
                  />
                  <MoreItem
                    Icon={Calendar}
                    label="Tag future-readiness"
                    testid="composer-more-future"
                    onClick={() => {
                      onOpenModal?.('future');
                      setMoreOpen(false);
                    }}
                  />
                  <MoreItem
                    Icon={Sparkles}
                    label="Hand off to artist"
                    testid="composer-more-handoff"
                    onClick={() => {
                      onOpenModal?.('handoff');
                      setMoreOpen(false);
                    }}
                  />
                  <div className="h-px my-1 bg-gold-dim/30" />
                  <MoreItem
                    Icon={XCircle}
                    label={canCancel ? 'Cancel booking' : 'Ask Ops to cancel'}
                    testid="composer-more-cancel"
                    onClick={() => {
                      onOpenModal?.('cancel');
                      setMoreOpen(false);
                    }}
                    blood
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>

        <div className="px-6 pb-4 pt-2 flex items-end gap-3">
          <div className="flex-1 min-w-0 flex flex-col gap-1">
          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              if (!e.target.value) {
                setTemplateId(undefined);
                setDraftDepositId(undefined);
              }
            }}
            placeholder={`Reply to ${customer.firstName} on ${channelSpec.label}…`}
            rows={2}
            className="w-full min-w-0 bg-ink-black border border-gold-dim/30 rounded-lg px-3 py-2 text-sm text-cream font-serif resize-none focus:outline-none focus:border-gold-bright"
            data-testid="composer-body"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
            {dm.mode === 'reply' ? (
              <span className="text-[12px] text-cream-soft" data-testid="composer-reply-helper">
                Replies within 24 hours of {customer.firstName}'s last message go straight to Instagram.
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void onSend()}
            disabled={!body.trim() || sending}
            className={clsx('text-xs', dm.mode === 'handoff' ? 'btn-ghost' : 'btn-primary')}
            data-testid="composer-send"
          >
            {dm.mode === 'human_agent' ? (
              <><UserRound size={12} /> Send as agent</>
            ) : dm.mode === 'handoff' ? (
              <><Copy size={12} /> Copy &amp; open Instagram</>
            ) : (
              <><Send size={12} /> Send</>
            )}
          </button>
        </div>
    </footer>
    <DepositLinkModal
      open={depositModalOpen}
      customer={customer}
      onClose={() => setDepositModalOpen(false)}
    />
    </>
  );
}

// The confirm popover for a fresh push (B8 / 75mx + 75nf, diagram 03). Pre-fills
// the name (blank for an IG placeholder), the phone (plain AU regex over the
// thread, editable, REQUIRED), a summary built from the thread by rules (editable)
// and a callback time (if "call me at 3pm" appears).
function PushForm({
  customer,
  clock,
  onCancel,
  onConfirm,
}: {
  customer: CustomerRecord;
  clock: string;
  onCancel: () => void;
  /** Returns null once pushed, else why it was refused (the form stays open). */
  onConfirm: (payload: PushLeadInput) => Extract<PushLeadResult, { ok: false }>['reason'] | null;
}) {
  const threadText = customer.conversation.map((e) => e.body).join('  ');
  const inboundText = customer.conversation
    .filter((e) => e.direction === 'in')
    .map((e) => e.body)
    .join('  ');
  const detectedPhone = extractAuPhone(threadText);
  const detectedCallbackIso = extractCallbackTime(inboundText, clock);

  // Native date/time inputs work in local time, so derive their values from the
  // local parts of the sim clock and rebuild the ISO the same way.
  const toDateInput = (iso: string): string => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const [phoneVal, setPhoneVal] = useState(detectedPhone ?? customer.phone ?? '');
  const placeholderName = isPlaceholderName(customer.firstName, customer.lastName);
  const [nameVal, setNameVal] = useState(
    placeholderName ? '' : `${customer.firstName} ${customer.lastName}`.trim(),
  );
  const [summaryVal, setSummaryVal] = useState(() => buildPushSummary(customer));
  const [error, setError] = useState<string | null>(null);
  // Callback is empty by default so a lead with no callback pushes straight to the
  // queue. Both fields prefill only when a "call me at 3pm"-style time was detected.
  // Setting a time is what creates the callback; a lone date does nothing.
  const [callbackDate, setCallbackDate] = useState(
    detectedCallbackIso ? toDateInput(detectedCallbackIso) : '',
  );
  const [callbackTime, setCallbackTime] = useState(
    detectedCallbackIso ? simTimeShort(detectedCallbackIso) : '',
  );

  const combineToIso = (dateStr: string, timeStr: string): string | undefined => {
    const [y, mo, da] = dateStr.split('-').map((n) => Number.parseInt(n, 10));
    const [h, mi] = timeStr.split(':').map((n) => Number.parseInt(n, 10));
    if (y == null || mo == null || da == null || h == null || Number.isNaN(h)) return undefined;
    const when = new Date(y, mo - 1, da, h, Number.isNaN(mi ?? NaN) ? 0 : (mi ?? 0), 0, 0);
    return when.toISOString();
  };

  const submit = () => {
    const [first = '', ...rest] = nameVal.trim().split(/\s+/).filter(Boolean);
    const refused = onConfirm({
      customerId: customer.id,
      phone: phoneVal.trim(),
      ...(first ? { firstName: first, lastName: rest.join(' ') } : {}),
      summary: summaryVal.trim() || undefined,
      // A time is required for a callback; if no date was picked, assume the sim day.
      pinnedCallbackAt: callbackTime
        ? combineToIso(callbackDate || toDateInput(clock), callbackTime)
        : undefined,
    });
    setError(
      refused === 'phone-required'
        ? 'Add their phone number to push. With no number they stay a warm lead here.'
        : refused === 'already-pushed'
          ? 'Already with a booking agent.'
          : refused
            ? 'Couldn’t push this lead.'
            : null,
    );
  };

  const fieldLabel = 'block text-[10px] uppercase tracking-widest text-cream-soft font-inter mb-1';
  const input =
    'w-full bg-ink-black border border-gold-dim/30 rounded-md px-2.5 py-1.5 text-sm text-cream font-serif focus:outline-none focus:border-gold-bright';

  return (
    <div
      className="absolute bottom-full mb-2 right-0 z-40 panel p-4 w-[300px] shadow-ink-soft"
      data-testid="composer-push-popover"
    >
      <p className="font-inter text-[11px] uppercase tracking-widest text-gold-bright">
        Push to agent queue
      </p>
      <p className="font-serif text-xs text-cream-soft mt-1 mb-3">
        Hands this lead to a booking agent. They pick it up in MyQ.
      </p>

      <div className="mb-3">
        <span className={fieldLabel}>Name</span>
        <input
          className={input}
          value={nameVal}
          onChange={(e) => setNameVal(e.target.value)}
          placeholder={placeholderName ? customerName(customer) : 'First and last name'}
          data-testid="composer-push-name"
        />
      </div>

      <div className="mb-3">
        <div className="flex items-baseline justify-between">
          <span className={fieldLabel}>Phone</span>
          <span className="text-[9px] uppercase tracking-widest text-gold-bright font-inter">required</span>
        </div>
        <input
          className={input}
          value={phoneVal}
          onChange={(e) => {
            setPhoneVal(e.target.value);
            setError(null);
          }}
          placeholder="No number on file"
          data-testid="composer-push-phone"
        />
        {detectedPhone ? (
          <p className="mt-1 flex items-center gap-1.5 text-[10px] text-mint font-inter">
            <Check size={11} /> Found in message
          </p>
        ) : null}
      </div>

      <div className="mb-3">
        <span className={fieldLabel}>Summary for the Agent</span>
        <textarea
          className={`${input} resize-none`}
          rows={3}
          value={summaryVal}
          onChange={(e) => setSummaryVal(e.target.value)}
          placeholder="What they want, in a line"
          data-testid="composer-push-summary"
        />
      </div>

      <div className="mb-1">
        <div className="flex items-baseline justify-between">
          <span className={fieldLabel}>Callback</span>
          <span className="text-[9px] uppercase tracking-widest text-cream-faint font-inter">optional</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            className={`${input} flex-1`}
            value={callbackDate}
            onChange={(e) => setCallbackDate(e.target.value)}
            data-testid="composer-push-callback-date"
          />
          <input
            type="time"
            className={`${input} w-[110px]`}
            value={callbackTime}
            onChange={(e) => setCallbackTime(e.target.value)}
            data-testid="composer-push-callback"
          />
        </div>
        {detectedCallbackIso ? (
          <p className="mt-1 flex items-center gap-1.5 text-[10px] text-mint font-inter">
            <Check size={11} /> Detected a callback time in the thread
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="mt-3 font-serif text-xs text-blood" role="alert" data-testid="composer-push-error">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end gap-2 mt-4">
        <button type="button" onClick={onCancel} className="btn-quiet text-[11px]">
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          className="btn-primary text-xs"
          data-testid="composer-push-confirm"
        >
          Push <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

// Shown when a sequence record already exists: no second push, just a jump link.
function PushDuplicate({ label, onJump }: { label: string; onJump: () => void }) {
  return (
    <div
      className="absolute bottom-full mb-2 right-0 z-40 panel p-4 w-[260px] shadow-ink-soft"
      data-testid="composer-push-duplicate"
    >
      <p className="font-inter text-sm text-gold-bright mb-1">
        {label.replace('In queue', 'Already in queue')}
      </p>
      <p className="font-serif text-xs text-cream-soft mb-3">A booking agent owns this lead.</p>
      <button
        type="button"
        onClick={onJump}
        className="btn-ghost text-[11px]"
        data-testid="composer-push-jump"
      >
        Jump to MyQ <ArrowRight size={12} />
      </button>
    </div>
  );
}

function MoreItem({
  Icon,
  label,
  onClick,
  testid,
  blood,
}: {
  Icon: typeof ScrollText;
  label: string;
  onClick: () => void;
  testid: string;
  blood?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={clsx(
        'flex w-full items-center gap-2 px-3 py-2 text-xs hover:bg-ink-soft/40 transition',
        blood ? 'text-blood' : 'text-cream-soft hover:text-cream',
      )}
    >
      <Icon size={12} /> {label}
    </button>
  );
}
